// @flow
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'app/common/ui/Button';
import { IconBtnPlug } from 'app/components/_Icons';

import IconKey from '../icons/IconKey';
import LinkButton from '../LinkButton';
import Confirmation from '../Confirmation';
import enhanceConf from '../Confirmation/confirmationState';
import buttonsState from './buttonsState';
import type { Sub } from '../Tariff';
import styles from './ButtonsExtended.scss';


type Props = {
  sub: Sub,
  stopping: boolean,
  pausingResuming: boolean,
  subscribing: boolean,
  saving: boolean,
  operating: boolean,

  handlePayToday: (e: SyntheticEvent<HTMLElement>) => void,
  handlePause: (e: SyntheticEvent<HTMLElement>) => void,
  handleStop: (e: SyntheticEvent<HTMLElement>) => void,
  handleSubscribe: (e: SyntheticEvent<HTMLElement>) => void,
  handleSettings: (e: SyntheticEvent<HTMLElement>) => void,
  handleChange: (e: SyntheticEvent<HTMLElement>) => void,
  handleCancel: (e: SyntheticEvent<HTMLElement>) => void,
  handleSave: (e: SyntheticEvent<HTMLElement>) => void,
  footnotes: Object,

  buttons: Array<string>,
  beginConfirmation: (e: SyntheticEvent<HTMLElement>) => void,
  stopConfirmation: (void) => void,

  subscribeText?: string,
  handleConnectChange: (e: SyntheticEvent<HTMLElement>) => void,
  connectEnabled: boolean,
};

//
// Состояния:
// 0. Оригинал (Пауза, Настроить)
// 1. Настроить (Изменить отключить отмена)
// 2. Изменить (Сохранить отмена)
// 3. Подтверждение (При отключении и паузе)
// Глобально:
// 1. Есть подписка (Пауза, настроить)
// 2. Нет подписки (Подключить)
// 3. Нет подписки, режим редактирования (Сохранить отмена)
// 4. Режим подтверждения (Все кнопки скрыты, кроме текущей)

// buttonsState выдает в props массив id кнопок buttons, которые должны отображаться в текущем состоянии

const ButtonsExtended = ({
  sub,
  stopping,
  subscribing,
  saving,
  operating,

  handlePause,
  handleStop,
  handleSubscribe,
  handleSettings,
  handleChange,
  handleCancel,
  handleSave,
  footnotes,

  buttons,
  beginConfirmation,
  stopConfirmation,

  subscribeText: st,
  handleConnectChange,
  connectEnabled,
}: Props) => {
  const { t } = useTranslation('tariff');
  const subscribeText = st || t('subscribe_bt');

  return (
    <div className={styles.buttons}>

      {buttons.includes('pause')
        && <Confirmation
          onSubmit={handlePause}
          comment={t('pause_confirm', {
            action: t('pause_confirm_action', { context: sub.is_paused ? 'off' : 'on' }),
          })}
          beginConfirmation={beginConfirmation}
          stopConfirmation={stopConfirmation}
        >
          {handle =>
            <Button
              outline={!sub.is_paused}
              warning={!sub.is_paused}
              error={sub.is_paused}
              onClick={handle}
              disabled={operating}
              name="pause"
            >
              {t('pause_bt', { context: sub.is_paused ? 'on' : 'off' })}
              {!sub.is_paused && footnotes.pause && <sup>{footnotes.pause.index}</sup>}
              {sub.is_paused
                ? <span className={styles.iconPlay}>&#9654;</span>
                : <span className={styles.iconPause}>&#9616;&#9616;</span>
              }
            </Button>
          }
        </Confirmation>
      }

      {buttons.includes('settings')
        && <Button onClick={handleSettings} name="settings" disabled={operating}>
          {t('settings_bt')}
          <IconKey className={styles.icon} />
        </Button>
      }

      {/* Кнопки настроек */}
      {buttons.includes('change')
        // $FlowFixMe
        && <LinkButton href="" onClick={handleChange} name="change" primary disabled={operating}>
          {saving ? t('change_bt_loading') : t('change_bt')}
        </LinkButton>
      }
      {buttons.includes('stop')
        && <Confirmation
          onSubmit={handleStop}
          comment={t('stop_confirm')}
          beginConfirmation={beginConfirmation}
          stopConfirmation={stopConfirmation}
        >
          {handle => (
            // $FlowFixMe
            <LinkButton href="" onClick={handle} name="stop" disabled={operating}>
              {stopping ? t('stop_bt_loading') : t('stop_bt')}
              {footnotes.stop && <sup className={styles.fn}>{footnotes.stop.index}</sup>}
            </LinkButton>)}
        </Confirmation>
      }

      {/* Кнопка сохранения в состоянии редактирования подписки */}
      {buttons.includes('save')
        && <Confirmation
          onSubmit={handleSave}
          comment={t('save_confirm')}
          beginConfirmation={beginConfirmation}
          stopConfirmation={stopConfirmation}
        >
          {handle => (
            // $FlowFixMe
            <LinkButton href="" onClick={handle} name="save" primary disabled={operating}>
              {saving ? t('save_bt_loading') : t('save_bt')}
            </LinkButton>)}
        </Confirmation>
      }

      {/* Кнопка подключения */}
      {buttons.includes('tariff')
        && <Button warning onClick={handleConnectChange} name="tariff" disabled={operating}>
          {subscribing ? t('subscribe_bt_loading') : subscribeText}
          <IconBtnPlug className={styles.icon} />
        </Button>
      }

      {/* {buttons.includes('connect') && buttons.includes('cancel') && !subscribing
        && <span className={styles.comment}>Настройте подключаемый тариф</span>} */}

      {/* Режим редактирования в процессе подключения */}
      {buttons.includes('connect')
        && <Confirmation
          name="connect"
          onSubmit={handleSubscribe}
          comment={t('connect_confirm')}
          beginConfirmation={beginConfirmation}
          stopConfirmation={stopConfirmation}
        >
          {handle => (
            // $FlowFixMe
            <LinkButton primary onClick={handle} name="connect" disabled={operating || !connectEnabled}>
              {subscribing ? t('connect_bt_loading') : t('connect_bt')}
            </LinkButton>)}
        </Confirmation>
      }

      {/* Назад */}
      {buttons.includes('cancel')
        // $FlowFixMe
        && <LinkButton href="" onClick={handleCancel} name="cancel" disabled={operating}>
          {t('cancel_link')}
        </LinkButton>}

    </div>
  );
};

export default enhanceConf(buttonsState(ButtonsExtended));
